import Vue from 'vue'
import store from '../store/index.js'
import router from '../router'
import {
  AUTH_QUERY_KEY,
  AUTH_VIEW,
  isAuthView,
  getAuthViewFromRoute
} from './authQuery.js'

export { AUTH_QUERY_KEY, AUTH_VIEW, isAuthView, getAuthViewFromRoute }

/*
 * The `?auth=` query is the single source of truth for open/closed, so the device
 * back button pops the popup instead of leaving the page. Everything here that is
 * not serializable (or that must never survive a reload) lives in this observable
 * instead of Vuex, because the store is persisted to localStorage wholesale.
 */
const options = Vue.observable({
  redirect: null,
  message: '',
  onSuccess: null,
  didPushHistory: false
})

export function getAuthOptions() {
  return options
}

export function consumeAuthSuccessHandler() {
  const handler = options.onSuccess
  options.onSuccess = null
  return handler
}

export function resetAuthOptions() {
  options.redirect = null
  options.message = ''
  options.onSuccess = null
  options.didPushHistory = false
}

function ignoreNavigationError() {}

function buildQueryWithView(view) {
  const query = Object.assign({}, router.currentRoute.query)
  query[AUTH_QUERY_KEY] = view
  return query
}

function buildQueryWithoutView() {
  const query = Object.assign({}, router.currentRoute.query)
  delete query[AUTH_QUERY_KEY]
  return query
}

function canOpenAuthModal() {
  if (Vue.prototype.$useCommunityAuth && Vue.prototype.$useCommunityAuth()) {
    Vue.prototype.$loginWithCommunity()
    return false
  }
  if (!store.getters.getMerchant.use_user_menu) return false
  if (store.getters.isLoggedIn) return false
  return true
}

/**
 * Stores the popup options without touching history. Router guards use this when
 * they are about to navigate themselves and cannot afford a nested push.
 * @returns {boolean} false when the popup must not open (community auth, guest
 * menu disabled, or the user is already signed in)
 */
export function prepareAuthModal(config) {
  const settings = config || {}
  if (!canOpenAuthModal()) return false

  options.redirect = settings.redirect || null
  options.message = settings.message || ''
  options.onSuccess = typeof settings.onSuccess === 'function' ? settings.onSuccess : null
  return true
}

export function openAuthModal(config) {
  const settings = config || {}
  if (!prepareAuthModal(settings)) return

  const view = isAuthView(settings.view) ? settings.view : AUTH_VIEW.LOGIN
  if (getAuthViewFromRoute(router.currentRoute) === view) return

  options.didPushHistory = true
  router.push({ query: buildQueryWithView(view) }).catch(ignoreNavigationError)
}

/* Replace, not push: one back press should close the popup, not walk between views */
export function setAuthView(view) {
  if (!isAuthView(view)) return
  if (getAuthViewFromRoute(router.currentRoute) === view) return
  router.replace({ query: buildQueryWithView(view) }).catch(ignoreNavigationError)
}

export function closeAuthModal() {
  if (!getAuthViewFromRoute(router.currentRoute)) {
    resetAuthOptions()
    return
  }

  /* pop the entry we pushed so the history stack stays clean */
  if (options.didPushHistory) {
    options.didPushHistory = false
    router.back()
    return
  }

  router.replace({ query: buildQueryWithoutView() }).catch(ignoreNavigationError)
}

/**
 * Resolves a successful authentication: runs the caller's handler, then either
 * navigates to the requested target or simply closes the popup in place.
 */
export function finishAuthModal() {
  const redirect = options.redirect
  const handler = consumeAuthSuccessHandler()

  if (handler) handler()

  if (!redirect) {
    closeAuthModal()
    return
  }

  const target = Object.assign({}, redirect)
  target.params = Object.assign(
    { merchant_code: store.getters.getMerchant.code },
    redirect.params || {}
  )

  /* replace, not push: the ?auth= entry is consumed so back never reopens the popup */
  resetAuthOptions()
  router.replace(target).catch(ignoreNavigationError)
}



// WEBPACK FOOTER //
// ./src/utils/authModal.js